import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { updatePreTrackingSoldOut } from 'services/scratcherService';

type PreTrackingPack = {
  game_id: string;
  pack_id: string;
};

type BulkPreTrackingPayload = {
  packs: PreTrackingPack[];
  is_sold_out_before_inventory_tracking: boolean;
};

export function useBulkPreTrackingSoldOutMutation(storeId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkPreTrackingPayload) => {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      const results = await Promise.all(
        payload.packs.map((pack) =>
          updatePreTrackingSoldOut(
            storeId,
            pack.game_id,
            pack.pack_id,
            payload.is_sold_out_before_inventory_tracking
          )
        )
      );

      return {
        results,
        total: payload.packs.length,
        nextValue: payload.is_sold_out_before_inventory_tracking,
      };
    },
    onSuccess: async ({ results, total, nextValue }) => {
      const failed = results.filter((result) => !result.success).length;
      if (failed > 0) {
        toast.error(`${failed} pack${failed === 1 ? '' : 's'} could not be updated`);
      } else {
        toast.success(
          nextValue
            ? `${total} pack${total === 1 ? '' : 's'} excluded from inventory tracking`
            : `${total} pack${total === 1 ? '' : 's'} included in inventory tracking`
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['current-inventory-status', storeId] });
      await queryClient.invalidateQueries({ queryKey: ['lottery-packs', storeId] });
    },
  });
}
